const COLLECTION_SECTION_SELECTOR = '[data-collection-section]';
const COLLECTION_HEADER_SELECTOR = '[data-collection-header]';
const COLLECTION_HEADER_FILTERS_SELECTOR =
  '[data-collection-header-filters]';
const COLLECTION_PRODUCTS_SELECTOR = '[data-collection-products]';
const FIXED_STATE = 'fixed';
const STATIC_STATE = 'static';
const controllers = new WeakMap();

const queryWithin = (root, selector) =>
  (root?.matches?.(selector) ? root : root?.querySelector?.(selector)) || null;

/**
 * Keeps the collection filter controls below the site header while the product
 * grid is in view. Both Shopify and Constructor collections use this module:
 * the latter intentionally does not download `js_page-collection.js`.
 */
export const initCollectionHeaderFilters = (element = document) => {
  const document = element?.ownerDocument || element;
  const section =
    element?.closest?.(COLLECTION_SECTION_SELECTOR) || element || document;
  const headerFiltersElement = queryWithin(
    section,
    COLLECTION_HEADER_FILTERS_SELECTOR
  );
  const resultsContainer = queryWithin(section, COLLECTION_PRODUCTS_SELECTOR);
  const header = queryWithin(section, COLLECTION_HEADER_SELECTOR);
  const window = document?.defaultView || globalThis.window;

  if (!headerFiltersElement || !resultsContainer || !header || !window) {
    return null;
  }

  const existing = controllers.get(headerFiltersElement);

  if (existing) {
    existing.refresh();
    return existing;
  }

  const spacer = document.createElement('div');
  spacer.setAttribute('data-collection-header-filters-spacer', '');
  spacer.setAttribute('aria-hidden', 'true');
  // The bar is right-aligned with `ml-auto`; its stand-in has to be too.
  spacer.style.marginLeft = 'auto';

  let active = true;
  let containerBottom = 0;
  let headerFiltersHeight = 0;
  let headerFiltersThreshold;
  let measureFrame;
  let reservedHeight = 0;
  let reservedWidth = 0;
  let resizeTimeout;
  let scrollFrame;

  const requestFrame = (callback) =>
    window.requestAnimationFrame?.(callback) ?? window.setTimeout(callback, 0);
  const cancelFrame = (frame) => {
    if (frame === undefined) {
      return;
    }

    if (window.cancelAnimationFrame) {
      window.cancelAnimationFrame(frame);
    } else {
      window.clearTimeout(frame);
    }
  };
  const getScrollTop = () =>
    document.documentElement?.scrollTop ||
    document.body?.scrollTop ||
    window.scrollY ||
    0;

  // Only measurable while the bar is still in the collection header flow.
  const measureHeaderFilters = () => {
    if (headerFiltersElement.dataset.collectionHeaderState === FIXED_STATE) {
      return;
    }

    const rect = headerFiltersElement.getBoundingClientRect();
    reservedWidth = rect.width;
    reservedHeight = rect.height;
  };

  const setState = (state) => {
    if (headerFiltersElement.dataset.collectionHeaderState === state) {
      return;
    }

    if (state === FIXED_STATE) {
      if (reservedWidth && reservedHeight) {
        spacer.style.width = `${reservedWidth}px`;
        spacer.style.height = `${reservedHeight}px`;
        headerFiltersElement.before(spacer);
      }
    } else {
      spacer.remove();
    }

    headerFiltersElement.dataset.collectionHeaderState = state;
  };

  const updateState = () => {
    scrollFrame = undefined;

    if (!active || headerFiltersThreshold === undefined) {
      return;
    }

    if (!reservedHeight) {
      measureHeaderFilters();
    }

    const scrollTop = getScrollTop() + 100;
    const headerBottomPosition = scrollTop + headerFiltersHeight;
    const isWithinContainer =
      scrollTop >= headerFiltersThreshold &&
      headerBottomPosition < containerBottom;

    setState(isWithinContainer ? FIXED_STATE : STATIC_STATE);
  };

  const refresh = () => {
    cancelFrame(measureFrame);
    measureFrame = requestFrame(() => {
      measureFrame = undefined;

      if (!active) {
        return;
      }

      const scrollY = window.scrollY || getScrollTop();
      headerFiltersThreshold = header.getBoundingClientRect().top + scrollY;
      headerFiltersHeight = headerFiltersElement.offsetHeight;
      containerBottom =
        resultsContainer.getBoundingClientRect().bottom + scrollY;
      measureHeaderFilters();
      updateState();
    });
  };

  const onScroll = () => {
    if (scrollFrame === undefined) {
      scrollFrame = requestFrame(updateState);
    }
  };

  const onResize = () => {
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(refresh, 200);
  };

  const destroy = () => {
    if (!active) {
      return;
    }

    active = false;
    cancelFrame(measureFrame);
    cancelFrame(scrollFrame);
    window.clearTimeout(resizeTimeout);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('DOMContentLoaded', refresh);
    window.removeEventListener('load', refresh);
    spacer.remove();
    headerFiltersElement.dataset.collectionHeaderState = STATIC_STATE;
    controllers.delete(headerFiltersElement);
  };

  const controller = { destroy, refresh };

  controllers.set(headerFiltersElement, controller);
  window.addEventListener('scroll', onScroll);
  window.addEventListener('resize', onResize);
  window.addEventListener('DOMContentLoaded', refresh);
  window.addEventListener('load', refresh);
  document.fonts?.ready?.then(refresh);
  refresh();

  return controller;
};

export default initCollectionHeaderFilters;
